import { ProcessedOrderbookEntry, RawOrderbookEntry } from '../model/types';

/**
 * Calculates cumulative amounts and percentages for orderbook display
 */
export function calculateOrderbookPercentages(
  entries: RawOrderbookEntry[],
  _side: 'ask' | 'bid',
): ProcessedOrderbookEntry[] {
  if (entries.length === 0) {
    return [];
  }

  const processed: ProcessedOrderbookEntry[] = [];
  let cumulativeBaseAmount = 0;
  let cumulativeQuoteAmount = 0;

  for (const entry of entries) {
    const price = Number(entry.price);
    const amount = Number(entry.amount);

    if (!isFinite(price) || !isFinite(amount) || amount <= 0) {
      continue;
    }

    const levelQuote = price * amount;

    cumulativeBaseAmount += amount;
    cumulativeQuoteAmount += levelQuote;

    processed.push({
      price: entry.price,
      amount: entry.amount,
      total: cumulativeQuoteAmount.toString(),
      currentAmount: cumulativeBaseAmount,
      percentage: 0,
      ordersCurrentAmountQuote: cumulativeQuoteAmount,
    });
  }

  if (processed.length === 0) {
    return [];
  }

  const referenceQuote = processed[processed.length - 1]?.ordersCurrentAmountQuote ?? 0;
  const referenceBase = processed[processed.length - 1]?.currentAmount ?? 0;
  const referenceValue = referenceQuote > 0 ? referenceQuote : referenceBase;

  if (referenceValue === 0) {
    return processed.map(entry => ({
      ...entry,
      percentage: 0,
    }));
  }

  return processed.map(entry => {
    const currentReference =
      referenceQuote > 0 ? (entry.ordersCurrentAmountQuote ?? 0) : entry.currentAmount;

    const rawPercentage = (currentReference / referenceValue) * 100;
    const percentage = Math.min(100, Math.max(2, Math.round(rawPercentage)));

    return {
      ...entry,
      percentage,
    };
  });
}

/**
 * Formats number for display with appropriate precision
 */
export function formatOrderbookNumber(value: number | string, maxDecimals = 8): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) return '0';

  // For very small numbers, use exponential notation
  if (num > 0 && num < 0.00001) {
    return num.toExponential(2);
  }

  // For large numbers, use K/M notation
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }

  // Regular formatting with max decimals
  const str = num.toFixed(maxDecimals);
  // Remove trailing zeros after decimal point
  return str.replace(/\.?0+$/, '');
}
